import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SearchBar } from "@/components/SearchBar";
import { CostBreakdown } from "@/components/CostBreakdown";
import { HotelCard, RestaurantCard, ActivityCard } from "@/components/Cards";
import { ExploreSkeleton } from "@/components/Skeletons";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { Sun, Globe2, Tag, MapPinned, ShieldCheck, Database } from "lucide-react";

const TABS = [
    { id: "overview", label: "Overview" },
    { id: "hotels", label: "Hotels" },
    { id: "restaurants", label: "Restaurants" },
    { id: "activities", label: "Activities" },
    { id: "itinerary", label: "Itinerary" },
];

export default function Explore() {
    const [params] = useSearchParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [tab, setTab] = useState("overview");

    const query = useMemo(() => ({
        destination: params.get("destination") || "",
        days: Number(params.get("days") || 5),
        travelers: Number(params.get("travelers") || 2),
        budget_level: params.get("budget_level") || "mid",
        origin: params.get("origin") || null,
    }), [params]);

    useEffect(() => {
        if (!query.destination) { setLoading(false); return; }
        let aborted = false;
        setLoading(true); setError(""); setData(null);
        api.post("/search", query)
            .then(({ data }) => { if (!aborted) setData(data); })
            .catch((e) => { if (!aborted) setError(formatApiError(e.response?.data?.detail) || e.message); })
            .finally(() => { if (!aborted) setLoading(false); });
        return () => { aborted = true; };
    }, [query]);

    const saveItem = async (item_type, name, payload) => {
        if (!user || !user._id) {
            toast.error("Please sign in to save favourites");
            return;
        }
        try {
            await api.post("/favorites", {
                item_type,
                destination: data?.destination?.name || query.destination,
                name,
                payload,
            });
            toast.success("Saved to your favourites");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="App">
            <Toaster position="top-center" richColors closeButton />
            <Navbar />

            <section className="mx-auto max-w-6xl px-6 pt-10">
                <SearchBar variant="compact" />
            </section>

            {loading && (
                <ExploreSkeleton destination={query.destination} />
            )}

            {!loading && error && (
                <div className="mx-auto mt-24 max-w-xl rounded-3xl border border-destructive/30 bg-destructive/5 p-6 text-center" data-testid="explore-error">
                    <p className="font-display text-2xl text-destructive">Something went sideways</p>
                    <p className="mt-2 text-sm text-moss">{error}</p>
                </div>
            )}

            {!loading && data && (
                <>
                    {/* Destination header */}
                    <section className="mx-auto mt-16 max-w-6xl px-6">
                        <span className="overline">your trip to</span>
                        <h1 data-testid="destination-title" className="mt-2 font-display text-5xl text-ink md:text-7xl">
                            {data.destination?.name}
                        </h1>
                        <p className="mt-5 max-w-2xl text-lg text-moss leading-relaxed">{data.destination?.summary}</p>
                        <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
                            <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1 ring-1 ring-jade/15 text-moss">
                                <Sun size={14} className="text-terracotta" /> {data.destination?.best_time}
                            </span>
                            <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1 ring-1 ring-jade/15 text-moss">
                                <Globe2 size={14} className="text-jade" /> {data.destination?.language} · {data.destination?.currency}
                            </span>
                            {(data.destination?.tags || []).map((t) => (
                                <span key={t} className="flex items-center gap-1.5 rounded-full bg-terracotta/10 px-3 py-1 text-terracotta">
                                    <Tag size={12} /> {t}
                                </span>
                            ))}
                        </div>
                        <Link
                            to={`/trips/new?seed=${encodeURIComponent(data.destination?.name || query.destination)}`}
                            data-testid="explore-add-to-trip"
                            className="mt-6 inline-flex btn-secondary"
                        >
                            <MapPinned size={14} className="mr-2" /> Add to a multi-city trip
                        </Link>
                    </section>

                    {/* Tabs + content */}
                    <section className="mx-auto mt-12 max-w-6xl px-6">
                        <div className="grid gap-10 lg:grid-cols-[1fr_320px]">
                            <div>
                                <div className="flex flex-wrap gap-2 border-b border-jade/15 pb-3" role="tablist">
                                    {TABS.map((t) => (
                                        <button
                                            key={t.id}
                                            data-testid={`tab-${t.id}`}
                                            onClick={() => setTab(t.id)}
                                            className={`pill !px-4 !py-2 text-sm ${tab === t.id ? "bg-ink text-sand" : "text-moss hover:text-ink"}`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="mt-8">
                                    {tab === "overview" && (
                                        <div className="grid gap-8 md:grid-cols-2" data-testid="tab-content-overview">
                                            <div className="rounded-3xl bg-white p-6 ring-1 ring-jade/10">
                                                <p className="overline">top picks</p>
                                                <h3 className="mt-2 font-display text-3xl text-ink">{(data.hotels || []).length} hotels · {(data.restaurants || []).length} restaurants</h3>
                                                <p className="mt-3 text-sm text-moss">All hand-curated for your {query.budget_level} budget, ready to explore.</p>
                                            </div>
                                            <div className="rounded-3xl bg-jade p-6 text-sand">
                                                <p className="overline !text-sand/80">a quick taste</p>
                                                <h3 className="mt-2 font-display text-3xl">{(data.activities || []).length} experiences</h3>
                                                <p className="mt-3 text-sm text-sand/80">From culture to adventure, with prices and durations to plan ahead.</p>
                                            </div>
                                            {(data.itinerary || []).slice(0, 4).map((d) => (
                                                <div key={d.day} className="rounded-3xl bg-white p-6 ring-1 ring-jade/10">
                                                    <p className="overline">day {d.day}</p>
                                                    <h4 className="mt-2 font-display text-2xl text-ink">{d.title}</h4>
                                                    <p className="mt-2 text-sm text-moss">{d.details}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {tab === "hotels" && (
                                        <div className="grid gap-6 sm:grid-cols-2" data-testid="tab-content-hotels">
                                            {(data.hotels || []).map((h, i) => (
                                                <HotelCard key={i} hotel={h} index={i} destination={data.destination?.name || query.destination} onSave={() => saveItem("hotel", h.name, h)} />
                                            ))}
                                        </div>
                                    )}

                                    {tab === "restaurants" && (
                                        <div className="grid gap-6 sm:grid-cols-2" data-testid="tab-content-restaurants">
                                            {(data.restaurants || []).map((r, i) => (
                                                <RestaurantCard key={i} restaurant={r} index={i} destination={data.destination?.name || query.destination} onSave={() => saveItem("restaurant", r.name, r)} />
                                            ))}
                                        </div>
                                    )}

                                    {tab === "activities" && (
                                        <div className="grid gap-6 sm:grid-cols-2" data-testid="tab-content-activities">
                                            {(data.activities || []).map((a, i) => (
                                                <ActivityCard key={i} activity={a} index={i} destination={data.destination?.name || query.destination} onSave={() => saveItem("activity", a.name, a)} />
                                            ))}
                                        </div>
                                    )}

                                    {tab === "itinerary" && (
                                        <ol className="relative ml-3 border-l-2 border-jade/20" data-testid="tab-content-itinerary">
                                            {(data.itinerary || []).map((d) => (
                                                <li key={d.day} className="relative ml-6 pb-8">
                                                    <span className="absolute -left-9 grid h-7 w-7 place-items-center rounded-full bg-terracotta text-sand text-xs font-medium">{d.day}</span>
                                                    <h4 className="font-display text-2xl text-ink">{d.title}</h4>
                                                    <p className="mt-1 text-sm text-moss">{d.details}</p>
                                                </li>
                                            ))}
                                        </ol>
                                    )}
                                </div>
                            </div>

                            <CostBreakdown
                                data={data.cost_breakdown_usd}
                                days={query.days}
                                travelers={query.travelers}
                                currency={data.destination?.currency}
                                fx={data.exchange_rate_usd}
                            />
                        </div>
                    </section>

                    {/* Data sources */}
                    {(data.data_sources || []).length > 0 && (
                        <section className="mx-auto mt-12 max-w-6xl px-6">
                            <div className="glass rounded-3xl p-5 md:p-6">
                                <div className="flex items-start gap-3">
                                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-jade/10 text-jade">
                                        <ShieldCheck size={16} />
                                    </span>
                                    <div>
                                        <p className="overline">data sources</p>
                                        <p className="mt-1 text-sm text-moss">
                                            Budget estimates use realistic 2024–2025 market averages — not exaggerated.
                                        </p>
                                        <ul className="mt-2 flex flex-wrap gap-2" data-testid="explore-sources">
                                            {data.data_sources.map((s, i) => (
                                                <li key={i} className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs text-ink ring-1 ring-jade/10">
                                                    <Database size={11} className="text-terracotta" /> {s}
                                                </li>
                                            ))}
                                            {data.curated && (
                                                <li className="rounded-full bg-jade px-3 py-1 text-xs text-sand">Werighty verified pricing</li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}
                </>
            )}

            <Footer />
        </div>
    );
}
